import { ColorClass, Icon, IconIdentifier } from '@aus-platform/design-system';
import { isNumber } from 'lodash';
import React, { useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { HeaderTitleContext } from 'shared/context';
import { RoutesEnum } from 'shared/routes';

type HeaderTitleProps = {
  text: string;
};

const routesWithNoBackButton = [
  RoutesEnum.Dashboard.toString(),
  RoutesEnum.Users.toString(),
  RoutesEnum.UserGroups.toString(),
  RoutesEnum.Projects.toString(),
  RoutesEnum.Reports.toString(),
  RoutesEnum.AccessTags.toString(),
];

export const HeaderTitle: React.FC<HeaderTitleProps> = ({ text }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { headerBackButtonRoute } = useContext(HeaderTitleContext);
  const showBackButton = !routesWithNoBackButton.some(route => location.pathname.startsWith(route));

  const onBackButtonClick = () => {
    /*
     * Prefer browser history so the previous page (e.g. /iterations with
     * applied search/filters/sort) is restored as it was. We read react-router's
     * own history index off `window.history.state.idx`:
     *   - 0 means we're at the first router-managed entry (deep link / paste
     *     into new tab / refresh). `navigate(-1)` here would call
     *     `history.go(-1)` and escape the SPA. Fall back to the hierarchical
     *     route the current page provided, and replace the current entry —
     *     otherwise repeated back-clicks would push parent routes on top of
     *     the deep-linked page and the next back would re-enter it.

     */
    const historyIdx = (window.history.state as { idx?: number } | null)?.idx;

    if (isNumber(historyIdx) && historyIdx > 0) {
      navigate(-1);
      return;
    }
    /*
     *   - >0 means there is an in-app entry behind us. `navigate(-1)` restores
     *     it with its URL params intact.
     */
    navigate(headerBackButtonRoute, { replace: true });
  };

  return (
    <div className="header-title header-txt-1 bold-txt">
      {showBackButton && (
        <Icon
          identifier={IconIdentifier.ArrowLeft}
          colorClass={ColorClass.Neutral300}
          onClick={onBackButtonClick}
          className="me-3 cursor-pointer"
        />
      )}
      <span>{text}</span>
    </div>
  );
};
